"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/auth"
import type { PaymentMethodSummary } from "@/lib/billing/types"

/**
 * The card a Stripe upgrade to `planId` will charge, fetched on every open (a card swapped in
 * the Customer Portal shows next time). `null` data = no card; a failed read counts as no card,
 * so the dialog offers "Add card" rather than charging blind.
 */
export function useUpgradePaymentMethodQuery(planId: string | null) {
  const { fetchWithAuth } = useAuth()

  return useQuery({
    queryKey: ["billing", "payment-method", planId],
    enabled: planId !== null,
    staleTime: 0,
    gcTime: 0,
    queryFn: async (): Promise<PaymentMethodSummary | null> => {
      try {
        const response = await fetchWithAuth("/api/billing/payment-method")
        if (!response.ok) return null
        return (await response.json() as { paymentMethod: PaymentMethodSummary | null }).paymentMethod
      } catch {
        return null
      }
    },
  })
}
